import React, { useState, useEffect, useRef, useCallback } from 'react';

/* DemoShowcase — product tour.
   Responsive poster card that opens the tour in a fullscreen modal.
   No autoplay. preload="metadata" only. Native controls in the modal. */

const LIME = '#c8f135';
const TEXT = '#f2f5ea';
const MUTED = '#a2b093';
const BORDER = 'rgba(255,255,255,0.09)';
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace';
const BODY = "'Space Grotesk', system-ui, sans-serif";

export default function DemoShowcase() {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => {
    if (videoRef.current) videoRef.current.pause();
    setOpen(false);
  }, []);

  // Hero "See the tour" trigger
  useEffect(() => {
    window.addEventListener('scl:play-tour', openModal);
    return () => window.removeEventListener('scl:play-tour', openModal);
  }, [openModal]);

  // Escape closes; lock body scroll while open; autoplay only after the user opened the modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    const v = videoRef.current;
    if (v) { v.currentTime = 0; v.play().catch(() => {}); }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, closeModal]);

  return (
    <div style={{ width: '100%', maxWidth: 900, margin: '0 auto' }} data-testid="demo-showcase">
      {/* Poster card */}
      <button
        onClick={openModal}
        data-testid="demo-overlay-play"
        aria-label="Play the product tour"
        style={{
          position: 'relative', display: 'block', width: '100%',
          aspectRatio: '16 / 10', borderRadius: 16, overflow: 'hidden',
          border: `1px solid ${BORDER}`, cursor: 'pointer', padding: 0,
          background: '#04050a',
          boxShadow: '0 40px 100px -30px rgba(0,0,0,0.8)',
        }}
      >
        <img
          src="/scalitos-tour-poster.jpg"
          alt="Preview of the ScalitOS product tour"
          loading="lazy"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }}
        />
        <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(4,5,10,0.1) 40%, rgba(4,5,10,0.75) 100%)' }} />
        <span aria-hidden="true" style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'clamp(60px, 9vw, 78px)', height: 'clamp(60px, 9vw, 78px)', borderRadius: '50%',
          background: LIME, display: 'grid', placeItems: 'center',
          boxShadow: '0 14px 50px -8px rgba(200,241,53,0.55)',
        }}>
          <svg width="24" height="24" viewBox="0 0 22 22" fill="none"><path d="M6 3l13 8-13 8V3z" fill="#04050a" /></svg>
        </span>
        <span style={{
          position: 'absolute', left: 16, bottom: 14, right: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontFamily: BODY, fontSize: 'clamp(13px, 2vw, 15px)', fontWeight: 600, color: TEXT, textAlign: 'left' }}>
            Watch the product tour
          </span>
          <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.16em', color: LIME, padding: '5px 10px', borderRadius: 999, background: 'rgba(4,5,10,0.7)', border: '1px solid rgba(200,241,53,0.3)' }}>
            0:26
          </span>
        </span>
      </button>
      <p style={{ marginTop: 12, textAlign: 'center', fontFamily: BODY, fontSize: 12.5, color: MUTED }}>
        Recorded inside the live app — Growth Plan, asset creation, campaign workspace and calendar.
      </p>

      {/* Fullscreen modal — z-index above any floating widget */}
      {open && (
        <div
          data-testid="demo-modal"
          role="dialog"
          aria-modal="true"
          aria-label="ScalitOS product tour"
          onClick={closeModal}
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483200,
            background: 'rgba(2,3,5,0.94)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 'clamp(8px, 3vw, 40px)',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', width: 'min(1100px, 100%)' }}>
            <button
              onClick={closeModal}
              data-testid="demo-modal-close"
              aria-label="Close tour"
              style={{
                position: 'absolute', top: -46, right: 0,
                width: 40, height: 40, borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)', border: `1px solid ${BORDER}`,
                color: TEXT, fontSize: 18, cursor: 'pointer', display: 'grid', placeItems: 'center',
              }}
            >
              ✕
            </button>
            <video
              ref={videoRef}
              data-testid="demo-video"
              src="/scalitos-tour.mp4"
              poster="/scalitos-tour-poster.jpg"
              preload="metadata"
              controls
              playsInline
              style={{ width: '100%', maxHeight: '82vh', borderRadius: 12, display: 'block', background: '#000', border: `1px solid ${BORDER}` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
